import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MeetingAlertComponent } from '@app/components/meeting-alert/meeting-alert.component';
import {
  getCurrentMeeting,
  hasActiveMeetingForProject,
} from '@app/components/meeting-alert/meeting-alert.helper';
import { SalesContactCardComponent } from '@app/components/sales-contact-card/sales-contact-card.component';
import { ScheduleMeetingComponent } from '@app/components/schedule-meeting/schedule-meeting.component';
import { ProjectAdminResponse } from '@app/models/admin.model';
import {
  MeetingListResponse,
  MeetingResponse,
} from '@app/models/meeting.model';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, forkJoin, map, startWith } from 'rxjs';
import { GeneralTabComponent } from './general-tab/general-tab.component';
import { IncomingTabComponent } from './incoming-tab/incoming-tab.component';

type Tab = 'general' | 'incomingOffers' | 'obligations';
interface TabDef {
  key: Tab;
  label: string;
}

@Component({
  selector: 'app-admin-project-details',
  standalone: true,
  imports: [
    CommonModule,
    DetailsPageHeaderComponent,
    ScheduleMeetingComponent,
    MeetingAlertComponent,
    ButtonComponent,
    GeneralTabComponent,
    IncomingTabComponent,
    TranslateModule,
    SalesContactCardComponent,
  ],
  templateUrl: './project-details.component.html',
  styleUrl: './project-details.component.scss',
})
export class AdminProjectDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectAdminService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  readonly project = signal<ProjectAdminResponse | null>(null);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  readonly activeTab = signal<Tab>('general');
  readonly tabs: ReadonlyArray<TabDef> = [
    { key: 'general', label: 'admin.allProjects.tabs.general' },
    { key: 'incomingOffers', label: 'admin.allProjects.tabs.incomingOffers' },
  ];

  readonly projectId = toSignal(
    this.route.paramMap.pipe(
      startWith(this.route.snapshot.paramMap),
      map((param) => param.get('projectId') ?? param.get('id') ?? ''),
    ),
    { initialValue: '' },
  );
  protected files = signal<any[]>([]);

  readonly meetings = signal<MeetingResponse[]>([]);
  readonly loadingMeetings = signal(false);

  readonly isEditing = signal(false);
  readonly isScheduleOpen = signal(false);
  readonly showSalesContact = computed<boolean>(() => {
    const project = this.project();
    return project !== null && project.contactUserId === null;
  });

  readonly hasActiveMeetingForProject = computed(() =>
    hasActiveMeetingForProject(this.meetings(), this.projectId()),
  );

  readonly currentMeeting = computed(() =>
    getCurrentMeeting(this.meetings(), this.projectId()),
  );
  readonly isScheduleDisabled = computed(() => {
    const meeting = this.currentMeeting();
    return (
      this.loadingMeetings() ||
      (!!meeting && meeting.status === 2) ||
      !this.project() ||
      this.showSalesContact()
    );
  });

  ngOnInit(): void {
    this.reloadAll();
  }

  onEdit(): void {
    this.router.navigate(['/admin', 'projects', this.projectId(), 'edit']);
  }

  onViewProject(): void {
    this.router.navigate(['/project', this.projectId()]);
  }

  reloadAll(): void {
    const projectId = this.projectId().trim();
    if (!projectId) return;

    this.isLoading.set(true);
    this.loadingMeetings.set(true);
    this.error.set(null);

    forkJoin({
      project: this.projectService.getProjectAdminById(projectId),
      meetings: this.projectService.getMeetingsAdmin({ project: projectId }),
      files: this.projectService.getFilesForProject(projectId),
    })
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.loadingMeetings.set(false);
        }),
      )
      .subscribe({
        next: ({ project, meetings, files }) => {
          this.project.set(project as ProjectAdminResponse);
          this.meetings.set((meetings as MeetingListResponse)?.data ?? []);
          this.files.set(files.data);
        },
        error: () => {
          this.error.set('LOAD_FAILED');
          this.meetings.set([]);
        },
      });
  }

  onCancelMeeting(meetingId: string): void {
    const projectId = this.projectId();
    if (!projectId) return;

    this.loadingMeetings.set(true);
    this.projectService
      .cancelMeetingByContact(meetingId)
      .pipe(finalize(() => this.loadingMeetings.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.scheduleMeeting.toasts.cancelled'),
          );
          this.reloadAll();
        },
        error: () => {
          this.toast.error(
            this.translate.instant('admin.scheduleMeeting.toasts.cancelFailed'),
          );
        },
      });
  }

  onMeetingDone(meetingId?: string): void {
    const id = meetingId ?? this.currentMeeting()?.id;
    if (!id) return;

    this.loadingMeetings.set(true);
    this.projectService
      .setMeetingToDone(id)
      .pipe(finalize(() => this.loadingMeetings.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.scheduleMeeting.toasts.markedDone'),
          );
          this.reloadAll();
        },
        error: () => {
          this.toast.error(
            this.translate.instant(
              'admin.scheduleMeeting.toasts.markDoneFailed',
            ),
          );
        },
      });
  }

  setTab(tab: Tab): void {
    this.activeTab.set(tab);
  }
  goBack(): void {
    const target = history.state?.from || '/admin/projects';
    this.router.navigate([target], { relativeTo: this.route });
  }

  title(): string {
    return this.project()?.name ?? '';
  }
}
